import { getCappedImageProps } from "../utils/capped-image-props";

/** Mobile thumb slot. The bitmap stays 440w so a phone never fetches 640w+. */
export const THUMB_WIDTH = 440;
export const THUMB_HEIGHT = 248;

/** Slot-accurate sizes — srcset is capped at 440w so mobile never fetches 640w+. */
export const THUMB_SIZES =
	"(max-width: 639px) calc(100vw - 2rem), (max-width: 991px) min(440px, calc((100vw - 2rem - 1.5rem) / 2)), 439px";

/**
 * Desktop cards are ~556 CSS px inside the 1200px container. A separate
 * source so a phone never picks these widths. Source art is 1200px wide.
 */
export const THUMB_DESKTOP_MEDIA = "(min-width: 992px)";
export const THUMB_DESKTOP_SIZES =
	"calc((min(1200px, 100vw) - 4rem - 1.5rem) / 2)";
const THUMB_DESKTOP_WIDTHS = [640, 750, 880, 1200];
const THUMB_DESKTOP_SLOT = 556;

/** q60 — small 440w cards; saves ~6 KiB vs q70 with no visible loss at index size. */
const THUMB_QUALITY = 60;
const THUMB_DESKTOP_QUALITY = 75;

function thumbImageProps(src, { priority = false } = {}) {
	const props = getCappedImageProps({
		src,
		alt: "",
		width: THUMB_WIDTH,
		height: THUMB_HEIGHT,
		sizes: THUMB_SIZES,
		quality: THUMB_QUALITY,
		priority,
		maxSlotWidth: THUMB_WIDTH,
		srcsetWidths: [440],
	});
	return { ...props, width: THUMB_WIDTH, height: THUMB_HEIGHT };
}

function desktopThumbProps(src) {
	return getCappedImageProps({
		src,
		alt: "",
		width: THUMB_WIDTH,
		height: THUMB_HEIGHT,
		sizes: THUMB_DESKTOP_SIZES,
		quality: THUMB_DESKTOP_QUALITY,
		priority: true,
		maxSlotWidth: THUMB_DESKTOP_SLOT,
		preserveSizes: true,
		srcsetWidths: THUMB_DESKTOP_WIDTHS,
	});
}

/** Preload href/srcSet aligned with the LCP thumb (440w mobile, sharp desktop). */
export function getVideoBlogIndexThumbPreload(src) {
	if (typeof src !== "string" || !src.startsWith("/")) return null;

	const mobileSizes = "calc(100vw - 2rem)";
	const { src: href, srcSet } = getCappedImageProps({
		src,
		alt: "",
		width: THUMB_WIDTH,
		height: THUMB_HEIGHT,
		sizes: mobileSizes,
		quality: THUMB_QUALITY,
		priority: true,
		maxSlotWidth: THUMB_WIDTH,
		srcsetWidths: [440],
	});
	if (!href) return null;

	const desktop = desktopThumbProps(src);

	return {
		href,
		imageSrcSet: srcSet,
		imageSizes: mobileSizes,
		desktopHref: desktop.src,
		desktopSrcSet: desktop.srcSet,
		desktopSizes: desktop.sizes,
	};
}

/** Plain img from getImageProps — reserved aspect ratio before CSS/images load. */
export default function VideoBlogIndexThumb({ src, alt, priority = false }) {
	if (!src) return null;

	const { className: generatedClass, style, loading, fetchPriority, ...rest } =
		thumbImageProps(src, { priority });
	const desktop = desktopThumbProps(src);

	const img = (
		// eslint-disable-next-line @next/next/no-img-element
		<img
			{...rest}
			alt={alt || ""}
			loading={priority ? "eager" : loading || "lazy"}
			fetchpriority={priority ? "high" : fetchPriority}
			className={[generatedClass, "tu-blog-index__thumb-img"]
				.filter(Boolean)
				.join(" ")}
			style={{ ...style, objectFit: "cover" }}
		/>
	);

	if (!desktop.srcSet) return img;

	return (
		<picture>
			<source
				media={THUMB_DESKTOP_MEDIA}
				srcSet={desktop.srcSet}
				sizes={desktop.sizes}
			/>
			{img}
		</picture>
	);
}
